import { useEffect, useState } from "react";
import { Download, Share, X } from "lucide-react";
import { useI18n } from "../i18n/LanguageContext";
import {
  getInstallPrompt,
  isIos,
  isStandalone,
  onInstallPrompt,
  promptInstall,
} from "../lib/installPrompt";

export default function InstallAppButton({ className = "", style }) {
  const { t } = useI18n();
  const [available, setAvailable] = useState(Boolean(getInstallPrompt()));
  const [showIos, setShowIos] = useState(false);

  useEffect(() => onInstallPrompt((e) => setAvailable(Boolean(e))), []);

  if (isStandalone()) return null;
  const ios = isIos();
  if (!available && !ios) return null;

  const handleClick = async () => {
    if (available) {
      await promptInstall();
      setAvailable(Boolean(getInstallPrompt()));
      return;
    }
    setShowIos(true);
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className={`br-btn-outline inline-flex items-center justify-center gap-2 ${className}`}
        style={style}
        data-testid="pwa-install-button"
      >
        <Download size={15} />
        {t("pwa.install")}
      </button>

      {showIos ? (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center px-5"
          style={{ background: "rgba(6,9,18,0.82)", backdropFilter: "blur(6px)" }}
          onClick={() => setShowIos(false)}
          data-testid="pwa-ios-modal"
        >
          <div
            className="br-card relative w-full max-w-[420px] px-7 py-8"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setShowIos(false)}
              className="absolute top-4 right-4 inline-flex items-center justify-center h-9 w-9"
              style={{ color: "var(--br-text-dim)" }}
              aria-label="Close"
              data-testid="pwa-ios-close"
            >
              <X size={18} />
            </button>
            <div className="br-overline">{t("pwa.ios_title")}</div>
            <div className="mt-5 space-y-5">
              <IosStep n="1" icon={<Share size={16} />}>{t("pwa.ios_step1")}</IosStep>
              <IosStep n="2" icon={<Download size={16} />}>{t("pwa.ios_step2")}</IosStep>
            </div>
            <p className="mt-6 text-[0.8rem] leading-relaxed text-[color:var(--br-text-dim)]">
              {t("pwa.ios_note")}
            </p>
          </div>
        </div>
      ) : null}
    </>
  );
}

function IosStep({ n, icon, children }) {
  return (
    <div className="flex items-start gap-4">
      <div
        className="flex-shrink-0 h-9 w-9 flex items-center justify-center border font-mono-fin text-[0.8rem]"
        style={{ borderColor: "var(--br-line-bright)", color: "var(--br-bronze)" }}
      >
        {n}
      </div>
      <div className="flex-1 text-[0.88rem] leading-relaxed text-[color:var(--br-parchment)]">
        <span className="inline-flex items-center gap-1.5 mr-1 align-middle" style={{ color: "var(--br-bronze)" }}>
          {icon}
        </span>
        {children}
      </div>
    </div>
  );
}
